import { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Loader2, ArrowLeft, CheckCircle } from 'lucide-react'
import { z } from 'zod'
import { requestPasswordReset } from '@/shared/lib/auth-api'
import { Logo } from '@/admin/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
type FormValues = { email: string }

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const tenantSlug = searchParams.get('tenant') || ''
  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login'
  const prefilledEmail = searchParams.get('email') || ''

  const fields = useMemo<FormField[]>(() => [
    {
      type: 'email',
      name: 'email',
      label: 'Email',
      placeholder: 'you@company.com',
      value: prefilledEmail,
      validationSchema: z.string().min(1, 'Enter a valid email').email('Enter a valid email'),
    },
  ], [prefilledEmail])
  const { Form } = useForm(
    {
      name: 'forgot-password-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as FormValues
      setLoading(true)
      try {
        // allauth never reveals whether the address exists; the email (if any)
        // deep-links back to this SPA via HEADLESS_FRONTEND_URLS.
        await requestPasswordReset(values.email, tenantSlug || undefined)
      } finally {
        setSent(true)
        setLoading(false)
      }
    },
    [tenantSlug, prefilledEmail],
  )

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-8">
          <Logo size="md" />
        </div>

        {sent ? (
          <div className="text-center">
            <CheckCircle className="mx-auto mb-4 h-8 w-8 text-[var(--status-ok)]" />
            <h2 className="mb-2 text-[20px] font-display text-[var(--ink)]">
              Check your email
            </h2>
            <p className="clara-small mb-6">
              If an account exists with that email, we've sent a link to reset your password.
            </p>
            <Button
              variant="outline"
              onClick={() => navigate(loginPath)}
              className="cursor-pointer"
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <>
            <h2 className="mb-1 text-[28px] leading-[1.1] font-display text-[var(--ink)]">
              Reset password
            </h2>
            <p className="clara-small mb-6">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <Form className="space-y-4" />
            <Button
              type="submit"
              form="forgot-password-form"
              disabled={loading}
              className="mt-4 w-full cursor-pointer rounded-[4px] bg-[var(--green)] py-2.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] hover:bg-[var(--green-hover)] disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                'Send reset link'
              )}
            </Button>

            <Button
              variant="link"
              onClick={() => navigate(loginPath)}
              className="mt-6 flex items-center gap-1.5 px-0 text-[11px] text-[var(--green)] cursor-pointer"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
